import { useState } from 'react'
import { Button } from '@cottage/design-system/internal-tooling/ui'
import { scheduleIsValid } from '../engine/cashPolicy'
import type { CashPolicy, CashPolicySplitSchedule, EvaluateContext } from '../engine/types'
import { formatCurrencyWhole } from '../projectionsFormat'

const TIER_COLUMNS = ['T1', 'T2', 'T3A', 'T3B', 'T4A', 'T4B']

type SplitStatusProps = {
  policy: CashPolicy
  schedule: CashPolicySplitSchedule | null
  context: EvaluateContext
  warnings: string[]
  mappingWarning?: string | null
  refreshing: boolean
  onRefresh: () => void
}

export default function SplitStatus({
  policy,
  schedule,
  context,
  warnings,
  mappingWarning,
  refreshing,
  onRefresh,
}: SplitStatusProps) {
  const [showPeriods, setShowPeriods] = useState(false)
  const pending = !scheduleIsValid(schedule, policy, 36, context)

  return (
    <div className="space-y-2">
      <p className="text-xs text-gray-700">
        {pending
          ? 'Pending — the waterfall runs when you Apply'
          : schedule
            ? `Computed ${schedule.computed_at.slice(0, 10)} from distribution #${schedule.basis.last_dist_num ?? context.last_dist_num ?? '—'}`
            : null}
      </p>
      <div className="flex flex-wrap items-center gap-2">
        <Button variant="outline" size="sm" disabled={refreshing} onClick={onRefresh}>
          {refreshing ? 'Refreshing…' : 'Refresh waterfall split'}
        </Button>
      </div>
      {schedule && schedule.periods.length > 0 ? (
        <button
          type="button"
          className="text-xs text-gray-600 underline"
          onClick={() => setShowPeriods((open) => !open)}
        >
          {showPeriods ? 'Hide by period' : 'Show by period'}
        </button>
      ) : null}
      {showPeriods && schedule ? (
        <div className="max-h-48 overflow-auto rounded border border-gray-200">
          <table className="w-full text-xs">
            <thead>
              <tr className="border-b border-gray-200 text-left text-gray-500">
                <th className="px-2 py-1">Date</th>
                {TIER_COLUMNS.map((tier) => (
                  <th key={tier} className="px-2 py-1 text-right">{tier}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {schedule.periods.map((period) => (
                <tr key={period.date} className="border-b border-gray-100">
                  <td className="px-2 py-1">{period.date}</td>
                  {TIER_COLUMNS.map((tier) => (
                    <td key={tier} className="px-2 py-1 text-right tabular-nums">
                      {formatCurrencyWhole(period.tiers[tier] ?? 0)}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : null}
      {mappingWarning ? (
        <p className="text-xs text-amber-800">{mappingWarning}</p>
      ) : null}
      {warnings.length > 0 ? (
        <ul className="list-disc space-y-0.5 pl-4 text-xs text-gray-600">
          {warnings.map((warning) => (
            <li key={warning}>{warning}</li>
          ))}
        </ul>
      ) : null}
    </div>
  )
}
